const OTHER_KEY = '\0other';

export const findIssueGroup = (groups, { other = false, reason = '', issueField = '', omitField = false } = {}) => {
  if (!groups) return null;
  if (other) return groups.find((group) => group.other) || null;
  const key = omitField ? reason || issueField : `${issueField}\0${reason}`;
  return groups.find((group) => group.key === key) || null;
};

const issueKey = (issue, omitField) => {
  const fieldId = issue.field_id || '';
  const reason = issue.reason_code || '';
  return omitField ? reason || fieldId : `${fieldId}\0${reason}`;
};

const issueLabel = (issue, omitField) => {
  const reasonLabel = (issue.label || '').trim() || 'Unspecified issue';
  const fieldLabel = (issue.field_label || '').trim();
  if (!omitField && fieldLabel) return `${fieldLabel}: ${reasonLabel}`;
  return reasonLabel;
};

/**
 * Group a technician's failed jobs by the finding that failed.
 * Returns null when the API did not attach issues, so the caller can keep
 * the flat job list. A group is `top` only when that issue actually repeats
 * and nothing else repeats more often.
 */
export const technicianIssueGroups = (jobs, { omitField = false } = {}) => {
  const list = Array.isArray(jobs) ? jobs : [];
  const tagged = list.some((job) => (job.issues || []).length > 0);
  if (!tagged) return null;

  const groups = new Map();
  const add = (key, label, job, meta) => {
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label,
        jobs: [],
        fieldId: meta.fieldId || '',
        fieldLabel: meta.fieldLabel || '',
        reasonCode: meta.reasonCode || '',
        reasonLabel: meta.reasonLabel || label,
        other: Boolean(meta.other),
      };
      groups.set(key, group);
    }
    group.jobs.push(job);
  };

  for (const job of list) {
    const issues = job.issues || [];
    if (!issues.length) {
      add(OTHER_KEY, 'Other failures', job, { other: true, reasonLabel: 'Other failures' });
      continue;
    }
    const seen = new Set();
    for (const issue of issues) {
      const key = issueKey(issue, omitField);
      if (seen.has(key)) continue;
      seen.add(key);
      add(key, issueLabel(issue, omitField), job, {
        fieldId: issue.field_id || '',
        fieldLabel: (issue.field_label || '').trim(),
        reasonCode: issue.reason_code || '',
        reasonLabel: (issue.label || '').trim() || 'Unspecified issue',
      });
    }
  }

  const ordered = [...groups.values()].sort((a, b) => {
    if (b.jobs.length !== a.jobs.length) return b.jobs.length - a.jobs.length;
    const other = (group) => (group.key === OTHER_KEY ? 1 : 0);
    if (other(a) !== other(b)) return other(a) - other(b);
    return a.label.localeCompare(b.label);
  });
  const topCount = ordered[0]?.jobs.length || 0;
  const recurring = topCount > 1;
  return ordered.map((group) => ({
    ...group,
    count: group.jobs.length,
    top: recurring && group.jobs.length === topCount,
  }));
};
